// ─── ¿Quién descuenta el inventario de una venta: el servidor o el cliente? ──
//
// Antes de la migración 0051 el descuento de stock corre EN EL CLIENTE
// (CarritoPOS / ModalDivisionCuenta → inventarioService.descontarPorVenta).
// Desde 0051 lo hace el servidor: un trigger sobre `sales` descuenta al cerrar
// la venta y existe la RPC `mezo_descontar_inventario_venta` (idempotente).
//
// El cliente no sabe qué migraciones corrió el dueño, así que tras cerrar la
// venta llama la RPC como SONDA:
//   - responde OK            → el servidor ya descontó (o acaba de hacerlo) → el
//                              cliente NO descuenta (sin doble descuento).
//   - "function does not exist" (404 / PGRST202 / 42883) → 0051 no está →
//                              el cliente descuenta como hoy.
//   - otro error             → la RPC existe (y con ella el trigger) → servidor.
//   - error de red           → no sabemos; si antes vimos que la RPC existe →
//                              servidor; si no → cliente (comportamiento actual).
//
// Lógica pura, sin red, para poder testear el criterio "no doble descuento".

export function esErrorRpcInexistente(error) {
  if (!error) return false;
  const code = String(error.code ?? '');
  if (code === 'PGRST202' || code === '42883') return true;
  const msg = String(error.message ?? error.details ?? error.hint ?? '');
  return /does not exist|could not find|not find the function|PGRST202|42883|\b404\b/i.test(msg);
}

export function esErrorDeRed(error) {
  if (!error) return false;
  const m = String(error.message ?? '').toLowerCase();
  return m.includes('failed to fetch') || m.includes('networkerror')
      || m.includes('network request failed') || m.includes('load failed');
}

// rpcConocida: true (ya vimos que existe) | false (ya vimos que NO existe) | null.
export function decidirModoDescuento({ data, error, rpcConocida = null } = {}) {
  if (!error) {
    return { modo: 'servidor', motivo: data?.ya_descontado ? 'ya_descontado' : 'rpc_ok', rpcExiste: true };
  }
  if (esErrorRpcInexistente(error)) {
    return { modo: 'cliente', motivo: 'rpc_no_existe', rpcExiste: false };
  }
  if (esErrorDeRed(error)) {
    return rpcConocida === true
      ? { modo: 'servidor', motivo: 'sin_red_rpc_conocida', rpcExiste: true }
      : { modo: 'cliente',  motivo: 'sin_red',              rpcExiste: rpcConocida };
  }
  // La función existe pero falló (p.ej. 'No autorizado' por una carrera de
  // sesión): el trigger ya corrió en el servidor → no duplicar en el cliente.
  return { modo: 'servidor', motivo: 'rpc_error', rpcExiste: true };
}

// Registro en memoria de las órdenes que el servidor ya descontó. Lo consulta
// descontarPorVenta: si la orden está marcada, el cliente no descuenta.
export function crearRegistroDescuentos() {
  const marcadas = new Set();
  return {
    marcarServidor(orderId) { if (orderId != null) marcadas.add(String(orderId)); },
    descontadoEnServidor(orderId) { return orderId != null && marcadas.has(String(orderId)); },
    debeDescontarEnCliente(orderId) { return !(orderId != null && marcadas.has(String(orderId))); },
    limpiar() { marcadas.clear(); },
  };
}
